import { useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import { CategoryRow } from "../components/CategoryRow";
import { EmptyState } from "../components/EmptyState";
import { PosterImage } from "../components/PosterImage";
import { useCatalog } from "../contexts/CatalogContext";
import type { Stream } from "../lib/types";

export default function CatchupPage() {
  const { catalog, activePlaylist } = useCatalog();
  const [active, setActive] = useState("All");

  if (!activePlaylist) {
    return (
      <PageLayout>
        <EmptyState
          title="Pick a playlist"
          hint="Add a playlist in Settings to use catchup."
          cta={{ to: "/settings", label: "Open settings" }}
        />
      </PageLayout>
    );
  }

  const streams = catalog?.catchup?.streams || [];
  // Build category list from the live categories the catchup channels reference,
  // preserving upstream order.
  const cats = (() => {
    const ids = new Set<string>();
    streams.forEach((s) => s.category_id && ids.add(s.category_id));
    return (catalog?.live.categories || []).filter((c) => ids.has(c.id));
  })();

  const filtered = active === "All" ? streams : streams.filter((s) => s.category_id === active);

  if (streams.length === 0) {
    return (
      <PageLayout>
        <div className="px-4 md:px-8 py-4">
          <h1 className="font-display text-[36px] sm:text-[44px] tracking-[-0.5px] text-text-primary mb-4">
            Catchup
          </h1>
          <EmptyState
            title="No catchup channels"
            hint="None of the channels in this playlist have a tv-archive available."
          />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="px-4 md:px-8 py-4">
        <div className="flex items-end justify-between gap-4 mb-2">
          <h1 className="font-display text-[36px] sm:text-[44px] tracking-[-0.5px] text-text-primary">
            Catchup
          </h1>
          <span className="text-[11px] font-mono uppercase tracking-[0.04em] text-text-secondary mb-2">
            {streams.length.toLocaleString()} channels
          </span>
        </div>
        <p className="text-[12px] font-ui text-text-secondary mb-4 max-w-2xl">
          Pick a channel to see what aired in the past few days, then play any program.
        </p>
        {cats.length > 0 && (
          <div className="mb-4">
            <CategoryRow categories={cats} active={active} onChange={setActive} />
          </div>
        )}
        <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5">
          {filtered.map((s: Stream) => (
              <Link
                key={s.id}
                to={`/catchup/${encodeURIComponent(s.id)}`}
                className="group block snap-start"
              >
                <div
                  className="relative aspect-video rounded-lg overflow-hidden bg-canvas border border-muted/30 group-hover:border-accent-gold/60 transition-fast"
                  style={{ containerType: "inline-size" }}
                >
                  <PosterImage
                    src={s.logo || s.cover}
                    alt={s.name}
                    imgClass="object-contain p-3 transition-standard group-hover:scale-[1.03]"
                    orientation="row"
                  />
                  <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-pill bg-accent-gold text-canvas text-[9px] font-ui font-bold uppercase tracking-[0.06em]">
                    Archive
                  </span>
                </div>
                <p className="mt-1.5 text-[12px] font-ui font-medium text-text-primary truncate">
                  {s.name}
                </p>
                {s.tv_archive_duration ? (
                  <p className="text-[10px] font-mono uppercase tracking-[0.04em] text-text-secondary">
                    {s.tv_archive_duration} day{s.tv_archive_duration > 1 ? "s" : ""} back
                  </p>
                ) : null}
              </Link>
            ))}
        </div>
      </div>
    </PageLayout>
  );
}
